<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.modelosResultado"
                            @change="loadFrom('modeloResultado')"
                            label="Modelo"
                            v-model="model.modeloResultado"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.classificacoes"
                            @change="loadFrom('classificacao')"
                            default-option-text="Todas"
                            label="Classificação"
                            show-default-option
                            text-field="descricao"
                            v-model="model.classificacaoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :default-option-text="isModeloSintetico ? 'Todos' : 'Selecione'"
                            :options="form.municipios"
                            :validations="validations.municipio"
                            @change="loadFrom('municipio')"
                            label="Município"
                            show-default-option
                            text-field="nome"
                            v-model="model.municipioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="isModeloAnalitico && !model.municipioId"
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="isModeloAnalitico && !model.municipioId"
                            :default-option-text="isModeloSintetico ? 'Todos' : 'Selecione'"
                            :options="form.bairros"
                            :validations="validations.bairro"
                            @change="loadFrom('bairro')"
                            label="Bairro"
                            show-default-option
                            text-field="nome"
                            v-model="model.bairroId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-select
                            :disabled="isModeloAnalitico && !model.bairroId"
                            :options="form.tiposLogradouro"
                            @change="loadFrom('tipoLogradouro')"
                            default-option-text="Todos"
                            label="Tipo de logradouro"
                            show-default-option
                            text-field="descricao"
                            v-model="model.tipoLogradouroId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="isModeloAnalitico && !model.bairroId"
                            :repository="repositories.relatorioPacienteBairro.logradouro"
                            :request-query-string-params="autocompleteQueryStringParams.logradouro"
                            :search-fields="autocompleteFields.logradouro"
                            @select="loadFrom('logradouro')"
                            label="Logradouro"
                            placeholder="Nome"
                            text-field="nome"
                            v-model="model.logradouro"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="isModeloAnalitico && !model.bairroId"
                            :repository="repositories.relatorioPacienteBairro.paciente"
                            :request-query-string-params="autocompleteQueryStringParams.paciente"
                            :search-fields="autocompleteFields.paciente"
                            @select="loadFrom('paciente')"
                            label="Paciente"
                            placeholder="Nome ou prontuário"
                            text-field="nome"
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-form-checkbox
                            :disabled="isModeloAnalitico && !model.bairroId"
                            :label="false"
                            @change="handleForaAreaChange"
                            v-model="model.foraArea"
                        >
                            Fora de área
                        </rkm-form-checkbox>
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :disabled="model.foraArea || (isModeloAnalitico && !model.bairroId)"
                            :options="form.areas"
                            @change="loadFrom('area')"
                            default-option-text="Todas"
                            label="Área"
                            show-default-option
                            text-field="descricao"
                            v-model="model.areaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :disabled="!model.areaId"
                            :options="form.microareas"
                            default-option-text="Todas"
                            label="Microárea"
                            show-default-option
                            text-field="descricao"
                            v-model="model.microareaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="auto">
                        <rkm-button
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :foot-clone="isModeloSintetico && itens.length > 0"
                :items="itens"
                :page-options="[10, 20, 50, 100]"
                :per-page="50"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
                class="table-resultado"
                no-footer-sorting
            >
                <template
                    v-if="isModeloSintetico"
                    #foot(bairroNome)
                >
                    <span></span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalPacientes)
                >
                    <span>{{ getSafely(() => totalizadores.totalPacientes, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalSexoMasculino)
                >
                    <span>{{ getSafely(() => totalizadores.totalSexoMasculino, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalSexoFeminino)
                >
                    <span>{{ getSafely(() => totalizadores.totalSexoFeminino, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalSexoIndeterminado)
                >
                    <span>{{ getSafely(() => totalizadores.totalSexoIndeterminado, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalIdadeZeroDoze)
                >
                    <span>{{ getSafely(() => totalizadores.totalIdadeZeroDoze, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalIdadeTrezeDezessete)
                >
                    <span>{{ getSafely(() => totalizadores.totalIdadeTrezeDezessete, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalIdadeDezoitoCinquentaNove)
                >
                    <span>{{ getSafely(() => totalizadores.totalIdadeDezoitoCinquentaNove, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalIdadeSessentaSetentaNove)
                >
                    <span>{{ getSafely(() => totalizadores.totalIdadeSessentaSetentaNove, 0) }}</span>
                </template>

                <template
                    v-if="isModeloSintetico"
                    #foot(totalIdadeOitentaMais)
                >
                    <span>{{ getSafely(() => totalizadores.totalIdadeOitentaMais, 0) }}</span>
                </template>

                <template #foot(total)>

                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
// Vuex.
import { mapActions } from 'vuex'

// Mixins.
import { repository, error, object, stringCase, file, formats, data } from '@/mixins'

export default {
    computed: {
        autocompleteQueryStringParams() {

            const params = this.prepareData()

            return {
                logradouro: { ...params },
                paciente: { ...params },
            }
        },
        autocompleteFields() {

            return {
                logradouro: [
                    { key: 'logradouro.nome', searchable: true },
                ],
                paciente: [
                    { key: 'cidadao.nome', searchable: true },
                    { key: 'paciente.prontuario', searchable: true },
                ],
            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        columns() {

            if (!this.loaded) {

                return []
            }

            if (this.model.modeloResultado === 'sintetico') {

                return [
                    { key: 'bairroNome', label: 'Bairro', sortable: true, fixed: true },
                    { key: 'totalPacientes', label: 'Total de pacientes', class: 'text-center', sortable: true },
                    { key: 'totalSexoMasculino', label: 'Sexo masculino', class: 'text-center', sortable: true },
                    { key: 'totalSexoFeminino', label: 'Sexo feminino', class: 'text-center', sortable: true },
                    { key: 'totalSexoIndeterminado', label: 'Sexo indeterminado', class: 'text-center', sortable: true },
                    { key: 'totalIdadeZeroDoze', label: '0-12', class: 'text-center', sortable: true },
                    { key: 'totalIdadeTrezeDezessete', label: '13-17', class: 'text-center', sortable: true },
                    { key: 'totalIdadeDezoitoCinquentaNove', label: '18-59', class: 'text-center', sortable: true },
                    { key: 'totalIdadeSessentaSetentaNove', label: '60-79', class: 'text-center', sortable: true },
                    { key: 'totalIdadeOitentaMais', label: '80+', class: 'text-center', sortable: true },
                ]
            }

            return [
                { key: 'pacienteProntuario', label: 'Prontuário', sortable: true },
                { key: 'pacienteCns', label: 'CNS', sortable: true },
                { key: 'pacienteCpf', label: 'CPF', sortable: true },
                { key: 'pacienteNome', label: 'Paciente', sortable: true, fixed: true },
                { key: 'pacienteIdade', label: 'Idade', class: 'text-center', sortable: true },
                { key: 'sexoDescricao', label: 'Sexo', sortable: true },
                { key: 'endereco', label: 'Endereço', sortable: true },
                { key: 'telefoneNumero', label: 'Telefone', sortable: true },
                { key: 'area', label: 'Área', sortable: true },
                { key: 'microarea', label: 'Microárea', sortable: true },
                { key: 'usuarioResponsavelNome', label: 'Usuário responsável pelo cadastro', sortable: true },
            ]
        },
        validations() {

            return {
                municipio: { required: !this.isModeloSintetico },
                bairro: { required: !this.isModeloSintetico },
            }
        },
        isModeloSintetico() {

            return this.model && this.model.modeloResultado === 'sintetico'
        },
        isModeloAnalitico() {

            return this.model && this.model.modeloResultado === 'analitico'
        }
    },
    created() {

        this.repositories = {
            relatorioPacienteBairro: this.getConectaSusRepository('cadastro.relatorioPacienteBairro'),
            impressao: this.getConectaSusRepository('impressao.cadastro'),
            exportacao: this.getConectaSusRepository('exportacao.cadastro'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: true,
            indeterminate: false,
            selectedCols: [
                'pacienteProntuario',
                'pacienteCns',
                'pacienteCpf',
                'pacienteIdade',
                'sexoDescricao',
                'endereco',
                'telefoneNumero',
                'area',
                'microarea',
                'usuarioResponsavelNome',
            ],
            sortBy: 'pacienteNome',
            sortDesc: false,
            itens: [],
            totalizadores: null,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        load() {

            this.setLoading()

            this.repositories.relatorioPacienteBairro.list()
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.modeloResultado', modelo => {

                if (modelo === 'sintetico') {

                    this.sortBy = 'bairroNome'

                    this.selectedCols = [
                        'totalPacientes',
                        'totalSexoMasculino',
                        'totalSexoFeminino',
                        'totalSexoIndeterminado',
                        'totalIdadeZeroDoze',
                        'totalIdadeTrezeDezessete',
                        'totalIdadeDezoitoCinquentaNove',
                        'totalIdadeSessentaSetentaNove',
                        'totalIdadeOitentaMais',
                    ]
                }
                else {

                    this.sortBy = 'pacienteNome'

                    this.selectedCols = [
                        'pacienteProntuario',
                        'pacienteCns',
                        'pacienteCpf',
                        'pacienteIdade',
                        'sexoDescricao',
                        'endereco',
                        'telefoneNumero',
                        'area',
                        'microarea',
                        'usuarioResponsavelNome',
                    ]
                }

                this.sortDesc = false
            })

            this.$watch('model.logradouro', logradouro => {

                if (!logradouro) {

                    this.loadFrom('logradouro')
                }
            })

            this.$watch('model.paciente', paciente => {

                if (!paciente) {

                    this.loadFrom('paciente')
                }
            })
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioPacienteBairro.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(currentField) {

            const fields = {
                modeloResultado: { form: 'modelosResultado', model: 'modeloResultado' },
                classificacao: { form: 'classificacoes', model: 'classificacaoId' },
                municipio: { form: 'municipios', model: 'municipioId' },
                unidade: { form: 'unidades', model: 'unidadeId' },
                bairro: { form: 'bairros', model: 'bairroId' },
                tipoLogradouro: { form: 'tiposLogradouro', model: 'tipoLogradouroId' },
                logradouro: { model: 'logradouro' },
                paciente: { model: 'paciente' },
                foraArea: { model: 'foraArea' },
                area: { form: 'areas', model: 'areaId' },
                microarea: { form: 'microareas', model: 'microareaId' },
            }

            let clear = false

            for (const field in fields) {

                if (clear && field !== 'foraArea') {

                    if ('form' in fields[field]) {

                        this.$set(this.form, fields[field].form, [])
                    }

                    this.$set(this.model, fields[field].model, null)
                }
                else if (field === currentField) {

                    clear = true
                }
            }
        },
        search() {

            this.setLoading()

            this.repositories.relatorioPacienteBairro.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.itens
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioPacienteBairro(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioPacienteBairro(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.createIdKeysToObject(data, ['logradouro', 'paciente'])

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        handleForaAreaChange() {

            if (this.model.foraArea) {

                this.form.areas = []
                this.model.areaId = null

                this.form.microareas = []
                this.model.microareaId = null
            }
            else {

                this.loadFrom('foraArea')
            }

        }
    },
    mixins: [repository, error, object, stringCase, file, formats, data],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}

.table-resultado >>> .table-footer {
    color: var(--gray-dark);
    font-weight: 600;
    background: #f1f1f1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}
</style>